/**
 * Hybrid command transport: direct REST on LAN/Tailscale when reachable,
 * Firestore command queue as fallback for remote/off-site browsers.
 */

import { addDoc, collection, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, CONTROLLER_ID } from '../firebase';

export const COMMAND_TTL_MS = 120_000;

export interface CommandAuthFields {
  requestedBy: string;
  requestedByEmail: string;
  requestedAt: string;
}

export type CommandTransport = 'rest' | 'firestore';

let controllerUrlCache: { url: string | null; timestamp: number } | null = null;
const CONTROLLER_URL_CACHE_TTL = 5 * 60 * 1000;

/** Attach caller identity to a command payload */
export async function withCommandAuth<T extends Record<string, unknown>>(
  payload: T
): Promise<T & CommandAuthFields> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return {
    ...payload,
    requestedBy: user.uid,
    requestedByEmail: user.email ?? '',
    requestedAt: new Date().toISOString(),
  };
}

async function getControllerApiUrl(): Promise<string | null> {
  const envUrl = import.meta.env.VITE_CONTROLLER_API;
  if (envUrl) return envUrl;

  if (controllerUrlCache && Date.now() - controllerUrlCache.timestamp < CONTROLLER_URL_CACHE_TTL) {
    return controllerUrlCache.url;
  }

  try {
    const controllerDoc = await getDoc(doc(db, 'controllers', CONTROLLER_ID));
    const url = (controllerDoc.data()?.apiUrl as string | undefined) ?? null;
    controllerUrlCache = { url, timestamp: Date.now() };
    return url;
  } catch {
    return null;
  }
}

async function isControllerReachable(baseUrl: string): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2000);
    const response = await fetch(`${baseUrl.replace(/\/$/, '')}/api/health`, {
      signal: controller.signal,
    });
    clearTimeout(timeout);
    return response.ok;
  } catch {
    return false;
  }
}

/** Prefer the caller's source (e.g. manual_irrigation) over the REST transport tag. */
export function resolveTransportSource(payload: Record<string, unknown>): string {
  return typeof payload.source === 'string' && payload.source.length > 0
    ? payload.source
    : 'ui-rest';
}

function withCommandTtl<T extends Record<string, unknown>>(payload: T): T & { issuedAt: string; expiresAt: string } {
  const issuedAt = new Date().toISOString();
  return {
    ...payload,
    issuedAt,
    expiresAt: new Date(Date.now() + COMMAND_TTL_MS).toISOString(),
  };
}

async function sendDeviceCommandRest(
  baseUrl: string,
  deviceId: string,
  payload: Record<string, unknown>,
): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  const response = await fetch(
    `${baseUrl.replace(/\/$/, '')}/api/controllers/${CONTROLLER_ID}/devices/${encodeURIComponent(deviceId)}/command`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ ...payload, source: resolveTransportSource(payload) }),
    },
  );
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`REST command failed (${response.status}): ${body}`);
  }
}

async function sendControllerCommandRest(
  baseUrl: string,
  payload: Record<string, unknown>,
): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  const response = await fetch(
    `${baseUrl.replace(/\/$/, '')}/api/controllers/${CONTROLLER_ID}/commands`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ ...payload, source: resolveTransportSource(payload) }),
    },
  );
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`REST command failed (${response.status}): ${body}`);
  }
  const data = (await response.json()) as { id?: string };
  return data.id ?? 'rest';
}

async function sendControllerCommandFirestore(
  payload: Record<string, unknown>,
): Promise<string> {
  const command = await withCommandAuth({
    status: 'pending',
    createdAt: serverTimestamp(),
    ...withCommandTtl(payload),
  });
  const ref = await addDoc(
    collection(db, 'controllers', CONTROLLER_ID, 'commands'),
    command,
  );
  return ref.id;
}

/** Write a controller command — REST when on LAN, Firestore when remote. */
export async function sendControllerCommand(
  payload: Record<string, unknown>,
): Promise<{ id: string; transport: CommandTransport }> {
  const baseUrl = await getControllerApiUrl();
  if (baseUrl && await isControllerReachable(baseUrl)) {
    try {
      if (payload.type === 'device' && typeof payload.deviceId === 'string') {
        await sendDeviceCommandRest(baseUrl, payload.deviceId, payload);
        return { id: 'rest', transport: 'rest' };
      }
      const id = await sendControllerCommandRest(baseUrl, payload);
      return { id, transport: 'rest' };
    } catch (err) {
      console.warn('[Commands] REST path failed, falling back to Firestore:', err);
    }
  }

  const id = await sendControllerCommandFirestore(payload);
  return { id, transport: 'firestore' };
}
